import { memo } from "react"
import { Card } from "../../shadcn/card"
import { Badge } from "../../shadcn/badge"
import { Package2 } from "lucide-react"

import { Product } from "@/src/types/Product";


interface ProductItemProps {
    product: Product
}

const ProductItem = memo(({ product }: ProductItemProps) => {
    const getStockSeverity = (available: number, minStock: number) => {
        const ratio = available / minStock
        if (ratio < 0.25) return "destructive"
        if (ratio < 0.5) return "warning"
        return "secondary"
    }

    const getStockLabel = (available: number, minStock: number) => {
        const ratio = available / minStock
        if (ratio < 0.25) return "Critique"
        if (ratio < 0.5) return "Faible"
        return "Adéquat"
    }

    // Prepare image URL with proper path
    const imageUrl = (process.env.REACT_APP_STORAGE_URL || "") + "/" + product.image
    
    // Custom variant badges with enhanced dark mode support
    const BadgeVariants = {
        destructive: "bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300 dark:hover:bg-red-900/60 dark:border dark:border-red-800/50",
        warning: "bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/60 dark:border dark:border-amber-800/50",
        secondary: "bg-green-100 text-green-700 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300 dark:hover:bg-green-900/60 dark:border dark:border-green-800/50"
    }

    const severity = getStockSeverity(product.total_available, product.min_stock)
    const badgeClass = BadgeVariants[severity as keyof typeof BadgeVariants]

    return (
        <Card className="gap-4 p-4 transition-all hover:bg-muted/30 dark:bg-neutral-800/70 dark:hover:bg-neutral-800 dark:border-neutral-700 animate-in fade-in-50 duration-300">
            <div className="flex items-center h-24 gap-5">

                <div className="h-16 w-16 rounded-lg overflow-hidden bg-muted dark:bg-neutral-700 flex-shrink-0 border dark:border-neutral-600 relative">
                    {imageUrl ? (
                        <img
                            src={imageUrl}
                            alt={product.name}
                            className="h-full w-full object-cover"
                            onError={(e) => {
                                e.currentTarget.src = "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTNuvafpok1f34VfmtLMX_0RYNYnJ-aSpv0qQ&s"
                            }}
                        />
                    ) : (
                        <div className="h-full w-full flex items-center justify-center bg-gray-100 dark:bg-neutral-800">
                            <Package2 className="h-8 w-8 text-gray-400 dark:text-neutral-500" />
                        </div>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-foreground dark:text-neutral-100 truncate">{product.name}</h4>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                        <Badge className={badgeClass}>
                            {getStockLabel(product.total_available, product.min_stock)}
                        </Badge>
                        <span className="text-xs font-medium dark:text-neutral-300">
                            {product.total_available} / {product.min_stock} min
                        </span>
                    </div>
                    <p className="text-xs text-muted-foreground dark:text-neutral-400 mt-1 truncate">
                        {product.barcode && `#${product.barcode}`}
                        {product.brand && ` • ${product.brand}`}
                    </p>
                </div>

                <div className="text-right text-xs">
                    {product.category && (
                        <span className="inline-block px-2 py-1 rounded-md bg-muted dark:bg-neutral-700 text-muted-foreground dark:text-neutral-300 mb-1">
                            {product.category}
                            {product.subcategory && ` / ${product.subcategory}`}
                        </span>
                    )}
                    {product.total_sold !== undefined && (
                        <p className="font-medium text-foreground dark:text-neutral-200">
                            {product.total_sold} vendu{product.total_sold > 1 ? "s" : ""}
                        </p>
                    )}
                </div>
            </div>

        </Card>
    )
})

export default ProductItem